import React from 'react'
import { useCookieConsent } from '../../_providers/CookieConsentProvider'

/**
 * The compliance gate for analytics: shown on first visit (before any click)
 * on every route — admin and public guest-facing alike, since a guest who
 * opens an RSVP link with no account is a real visitor who can be tracked
 * just as much as a logged-in organiser. Accept/decline are both real
 * choices (a bare "OK" is not compliant); nothing analytics-related loads
 * until "Accept" is pressed — see AppWithConfig.tsx, which only calls
 * initAnalytics() once status === 'accepted'.
 *
 * The small "Cookie preferences" link is always rendered (not just while
 * unanswered) so a visitor who already accepted or declined has a simple,
 * always-available way to withdraw/change their choice — clicking it resets
 * status to 'unanswered', which re-shows the banner immediately.
 */
export function CookieConsentBanner() {
  const { status, accept, decline, reset } = useCookieConsent()

  return (
    <>
      {status === 'unanswered' && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
          className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white px-4 py-4 sm:px-6"
        >
          <div className="mx-auto flex max-w-4xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-200">
              We use cookies to understand how Invite With Love is used, so we can improve it. We
              only turn this on if you say yes — see our{' '}
              <a href="/privacy" className="underline hover:text-white">
                Privacy Policy
              </a>
              .
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                data-testid="cookie-consent-decline"
                onClick={decline}
                className="rounded-md border border-gray-500 px-4 py-2 text-sm font-medium text-gray-100 hover:bg-gray-800"
              >
                Decline
              </button>
              <button
                type="button"
                data-testid="cookie-consent-accept"
                onClick={accept}
                className="rounded-md bg-rose-500 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
      {status !== 'unanswered' && (
        <button
          type="button"
          data-testid="cookie-consent-preferences"
          onClick={reset}
          className="fixed bottom-1 left-1 z-40 rounded bg-transparent px-2 py-1 text-xs text-gray-400 opacity-70 hover:opacity-100 hover:underline"
        >
          Cookie preferences
        </button>
      )}
    </>
  )
}

export default CookieConsentBanner
